import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableHead,
  withStyles,
  withWidth,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import PropTypes from "prop-types";
import React from "react";
import { confirmable, createConfirmation } from "react-confirm";
import QrReader from "react-qr-reader";
import {
  StyledTable,
  StyledTableCell,
  StyledTableCellBodyBold,
  StyledTableRow,
} from "../../../components/CustomStyles/CustomTable";
import CustomizedDialogs from "../../../components/FormDialog/FormDialog";
import { formatRupiah, handleError } from "../../../components/Utils/Utils";
import OrderService from "../../../Services/OrderService";
import "../style.css";
import { formatCurrency } from "../../../components/Utils/Currency";

const styles = (theme) => ({
  header: {
    textAlign: "center",
  },
  textField: {
    color: "#FFFFFF",
    width: "100%",
  },
  section: {
    color: "#8a8888",
    width: "100%",
    background: "#c1c1c1",
    padding: "5px",
  },
});

const Form = (props) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const { classes, width, data, cancel } = props;
  const [openDialog, setOpenDialog] = React.useState(true);
  const [qrString, setQRString] = React.useState(null);
  const [transaction, setTransaction] = React.useState("");
  const [orderIndex, setOrderIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [updated, setUpdated] = React.useState(false);
  const [error, setError] = React.useState("");

  const bottomDiv = React.useRef();

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const scrollToBottom = () => {
    if (bottomDiv && bottomDiv.current) {
      bottomDiv.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScan = (data) => {
    if (data !== qrString) {
      if (data !== null) {
        setQRString(data);
        setLoading(true);
        setUpdated(false);
        setOrderIndex(0);
        OrderService.getOrder(data)
          .then((response) => {
            setLoading(false);
            const transaction = response.data;
            const index = transaction.orders.findIndex(
              (row) => row.id === data
            );

            if (index > -1) {
              if (
                transaction?.orders[index]?.tenant?.property?.id ===
                user?.operational?.id
              ) {
                setTransaction(transaction);
                setOrderIndex(index);
              } else {
                setTransaction("");
                handleError(
                  "You don't have access to this transaction data !!!"
                );
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            setTransaction("");
            handleError(error);
          });
      }
    }
  };

  const handleUpdateProgress = () => {
    setUpdating(true);
    const id = transaction ? transaction.orders[orderIndex].id : "0";
    let params = [
      {
        op: "replace",
        path: "/progress",
        value: "ready",
      },
    ];

    OrderService.patchData(id, params)
      .then((response) => {
        setUpdating(false);
        setUpdated(true);
      })
      .catch((error) => {
        setUpdating(false);
        setUpdated(true);
        setError(error);
      });
  };

  const handleOnError = (err) => {
    handleError(err);
  };

  React.useEffect(scrollToBottom, [transaction]);

  return (
    <CustomizedDialogs
      disableBackdropClick
      maxWidth={false}
      fullScreen={true}
      title={props.title}
      open={openDialog}
      handleClose={handleCloseDialog}
    >
      <DialogContent classes={{ root: classes.content }} dividers>
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          justify="space-evenly"
        >
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <QrReader
              delay={100}
              showViewFinder={false}
              onError={handleOnError}
              onScan={handleScan}
              style={{ width: "100%" }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction="row"
          alignItems="flex-start"
          justify="space-evenly"
        >
          {loading && (
            <Grid item xs={6}>
              <div
                style={{
                  width: "100%",
                  height: "50vh",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "20px auto",
                }}
              >
                <CircularProgress />
                <span style={{ marginTop: 10 }}>Search order data</span>
              </div>
            </Grid>
          )}
          {!loading && (
            <div style={{ maxWidth: "100%" }}>
              <Grid item xs={12} style={{ paddingLeft: 12 }}>
                {transaction && (
                  <div>
                    {updated && !error && (
                      <Alert severity="success">Success Update Progress</Alert>
                    )}
                    {updated && error && (
                      <Alert severity="error">{error || ""}</Alert>
                    )}

                    {transaction?.orders[orderIndex]?.progress ===
                      "process" && (
                      <List style={{ width: "100%" }}>
                        <ListItem>
                          <ListItemText
                            primary={transaction?.unique_id}
                            secondary="Transaction Code"
                          />
                        </ListItem>
                        <Divider />
                        <ListItem>
                          <ListItemText
                            primary={transaction?.user?.name}
                            secondary="Customer Name"
                          />
                        </ListItem>
                        <Divider />
                        <ListItem>
                          <ListItemText
                            primary={
                              transaction?.orders[orderIndex]?.tenant?.property
                                ?.name
                            }
                            secondary="Mall"
                          />
                        </ListItem>
                        <Divider />
                        <ListItem>
                          <ListItemText
                            primary={transaction?.pickup?.name}
                            secondary="Pickup Point"
                          />
                        </ListItem>
                      </List>
                    )}
                    {transaction?.orders[orderIndex]?.progress !==
                      "process" && (
                      <Alert severity="error" style={{ marginTop: 25 }}>
                        Unable to update order data. Updating order to ready for
                        pickup only when order status is process.
                      </Alert>
                    )}
                  </div>
                )}
                {!transaction && qrString !== null && (
                  <Alert severity="error" style={{ marginTop: 25 }}>
                    Order not found
                  </Alert>
                )}
              </Grid>
              {transaction && (
                <Grid item xs={12}>
                  {transaction?.orders[orderIndex]?.progress === "process" && (
                    <Box
                      margin={1}
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "scroll",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "start",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={
                            transaction?.orders[orderIndex]?.tenant?.logo
                              ?.square
                          }
                          alt={transaction?.orders[orderIndex]?.tenant?.name}
                          style={{ width: 50, height: 50 }}
                        />
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: "bold",
                            marginLeft: 10,
                          }}
                        >
                          {transaction?.orders[orderIndex]?.tenant?.name}
                        </span>
                      </div>
                      <StyledTable size="small" aria-label="detail order">
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell width="80%">Item</StyledTableCell>
                            <StyledTableCell align="right">
                              Price
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {transaction?.orders[orderIndex]?.items
                            ? transaction?.orders[orderIndex]?.items.map(
                                (item, i) => {
                                  return (
                                    <StyledTableRow key={i}>
                                      <StyledTableCell>
                                        {item.qty} x ({item.name || ""})
                                        {item.note && (
                                          <React.Fragment>
                                            <br />
                                            <span
                                              style={{
                                                fontStyle: "italic",
                                                lineHeight: 2,
                                              }}
                                            >
                                              {item.note}
                                            </span>
                                          </React.Fragment>
                                        )}
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {formatCurrency(
                                          transaction?.orders[orderIndex]
                                            ?.tenant?.property?.currency,
                                          (
                                            item.qty *
                                            (item.discounted_price
                                              ? item.discounted_price
                                              : item.price.value)
                                          ).toString()
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  );
                                }
                              )
                            : ""}
                          <StyledTableRow>
                            <StyledTableCellBodyBold>
                              Service (
                              {
                                transaction?.orders[orderIndex]
                                  ?.service_charge_percentage
                              }
                              %)
                            </StyledTableCellBodyBold>
                            <StyledTableCellBodyBold align="right">
                              {formatCurrency(
                                transaction?.orders[orderIndex]?.tenant
                                  ?.property?.currency,
                                transaction?.orders[
                                  orderIndex
                                ]?.service_charge_amount.toString()
                              )}
                            </StyledTableCellBodyBold>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCellBodyBold>
                              Tax (
                              {
                                transaction?.orders[orderIndex]
                                  ?.tax_charge_percentage
                              }
                              %)
                            </StyledTableCellBodyBold>
                            <StyledTableCellBodyBold align="right">
                              {formatCurrency(
                                transaction?.orders[orderIndex]?.tenant
                                  ?.property?.currency,
                                transaction?.orders[
                                  orderIndex
                                ]?.tax_charge_amount.toString()
                              )}
                            </StyledTableCellBodyBold>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCellBodyBold
                              colSpan={2}
                              align="right"
                              style={{
                                borderBottom: "none",
                                fontSize: "larger",
                              }}
                            >
                              Total:{" "}
                              {formatCurrency(
                                transaction?.orders[orderIndex]?.tenant
                                  ?.property?.currency,
                                (
                                  transaction?.orders[orderIndex]
                                    ?.sub_total_amount || 0
                                ).toString()
                              )}
                            </StyledTableCellBodyBold>
                          </StyledTableRow>
                        </TableBody>
                      </StyledTable>
                    </Box>
                  )}
                </Grid>
              )}
              <div ref={bottomDiv} />
            </div>
          )}
        </Grid>
      </DialogContent>
      {!loading && transaction && (
        <DialogActions>
          <div className="btn-wrapper">
            <Button
              type="button"
              variant="contained"
              style={{ margin: "0 auto", width: "100%" }}
              color="primary"
              disabled={
                updating ||
                updated ||
                transaction?.orders[orderIndex]?.progress !== "process"
              }
              onClick={handleUpdateProgress}
            >
              Ready For Pickup
            </Button>
            {updating && (
              <CircularProgress size={24} className="btn-progress" />
            )}
          </div>
        </DialogActions>
      )}
    </CustomizedDialogs>
  );
};

Form.propTypes = {
  width: PropTypes.oneOf(["lg", "md", "sm", "xl", "xs"]).isRequired,
  cancel: PropTypes.func,
};
export function scannerFormPickup(title, data, options = {}) {
  return createConfirmation(confirmable(withStyles(styles)(withWidth()(Form))))(
    {
      title,
      data,
      ...options,
    }
  );
}
